import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  FeatureFlagsProvider,
  LoaderWidget,
  RootProvider,
  ServicesProvider,
  useFeatureFlagsContext,
  useRootContext,
  useServicesContext,
} from '@jarvis/react-portal-addons';
import { ToastProvider } from '@veneer/core';
import resources from './assets/locale/index';
import DashboardComponent from './components/Dashboard/index';
import AdminConsolidatedDashboard from './components/ConsolidatedDashboard/AdminDashboard';
import EndUserConsolidatedDashboard from './components/ConsolidatedDashboard/EndUserDashboard';
import { ONBOARDING_CENTER_STACK_HOSTNAMES, USER_ROLES } from './utils/constants';

const App = props => {
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const { consolidatedPortalDashbordLayout, consolidatedPortalDashboardEndUser } = useFeatureFlagsContext();
  const { pending: servicesPending } = useServicesContext();
  const { shell: { localization, criterion, navigation }, stack, properties } = useRootContext();
  const locale = localization?.locale;

  const {
    criterionKeys,
    utoRedirect,
  } = properties ?? {};
  const isAdminCriterionKey = criterionKeys?.isAdminCriterionKey ?? '';

  const getSoftwareURL = useCallback(() => {
    const [language, country] = locale.split('-');
    const hostname = ONBOARDING_CENTER_STACK_HOSTNAMES[stack];

    return `${hostname}/${country}/${language}/get-software`.toLowerCase();
  }, [locale, stack]);

  useEffect(() => {
    const handleInviteFlow = async () => {
      const inviteParam = new URLSearchParams(globalThis.location.search).get('invite');
      const isEndUser = !(await criterion.checkByCriterionKey(isAdminCriterionKey));

      setIsCheckingRole(false);
      setUserRole(isEndUser ? USER_ROLES.END_USER : USER_ROLES.ADMIN);

      if (!utoRedirect && inviteParam === 'true' && isEndUser && stack !== undefined) {
        globalThis.location.assign(getSoftwareURL());
      }
    };

    handleInviteFlow();
  }, [criterion, getSoftwareURL, locale, stack, isAdminCriterionKey, utoRedirect]);

  if (utoRedirect) {
    const shellPostLoginRedirect = new URLSearchParams(window.location.search);

    const postUtoRedirect = new URLSearchParams(window.location.search);
    postUtoRedirect.set('postLoginRedirect', '/home?invite=true');

    // Set Shell's postLoginRedirect param so that after login in via Shell,
    // it redirects the user back to the /uto route to complete the invite user flow.
    shellPostLoginRedirect.set('postLoginRedirect', `/uto?${postUtoRedirect.toString()}`);

    // Redirects the user to the /uto route to start the invite user flow.
    // Pass all the existing query params to the /uto route.
    navigation.push(`/uto?${shellPostLoginRedirect.toString()}`);

    return <LoaderWidget fullScreen />;
  }

  const showLoader = (servicesPending || isCheckingRole) && consolidatedPortalDashbordLayout;

  if (showLoader) {
    return <LoaderWidget fullScreen />;
  }

  if (!consolidatedPortalDashbordLayout) {
    return <DashboardComponent {...props} />;
  }

  if (userRole === USER_ROLES.END_USER && consolidatedPortalDashboardEndUser) {
    return <EndUserConsolidatedDashboard />;
  }

  return <AdminConsolidatedDashboard />;
};

const Dashboard = props => {
  const { shell, stack, properties } = props;

  const localization = shell?.localization || {};

  const flags = useMemo(
    () => [
      { key: 'consolidatedPortalCheckAdvancedViewGrant' },
      { key: 'consolidatedPortalDashboardEndUser' },
      { key: 'consolidatedPortalDashbordLayout' },
      { key: 'consolidatedPortalGetAppButton' },
      { key: 'consolidatedPortalGetHpAppCard' },
      { key: 'consolidatedPortalHpAllInCard' },
      { key: 'consolidatedPortalInstantInkCard' },
      { key: 'consolidatedPortalPrinterCarousel' },
      { key: 'consolidatedPortalSustainabilityCard' },
      { key: 'consolidatedPortalUsageDataTelemetryCheck' },
      { key: 'dashboardDetailUsageModalPrinterClick' },
      { key: 'dashboardHideColumnReorder' },
      { key: 'dashboardHideSegmentedControl' },
      { key: 'dashboardPrintAnywhereV2' },
      { key: 'dashboardShowFiltersDetailedUsage' },
      { key: 'dashboardShowNoDataYet' },
      { key: 'dashboardUsageDataUnitFromOrg' },
      { key: 'grantsSupport' },
      { key: 'printerSecurityRename' },
      { key: 'smartSecurityRemoval' },
    ],
    [],
  );

  return (
    <ToastProvider>
      <FeatureFlagsProvider
        clientKey="smb-portal"
        flags={flags}
      >
        <RootProvider
          stack={stack}
          shell={shell}
          localization={localization}
          resources={resources}
          properties={properties}
        >
          <ServicesProvider shell={shell} stack={stack}>
            <App {...props} />
          </ServicesProvider>
        </RootProvider>

      </FeatureFlagsProvider>
    </ToastProvider>
  );
};

Dashboard.defaultProps = {
  shell: {},
  properties: {},
};

Dashboard.propTypes = {
  shell: PropTypes.objectOf(PropTypes.any),
  stack: PropTypes.number.isRequired,
  properties: PropTypes.objectOf(PropTypes.any),
};

App.propTypes = {
  shell: PropTypes.objectOf(PropTypes.any).isRequired,
  stack: PropTypes.number.isRequired,
};

export default Dashboard;
